import { useEffect, useRef } from "react"

import { history } from "../../utils/history"
import { MESSAGE_MODALS } from "../../context/custom/MessageModalContext"
import { useManagedContext } from "./useManagedContext"

const CLOSE_REARM_DELAY = 250

/**
 * Bloquea toda salida de la vista mientras haya cambios sin guardar: navegación
 * interna (menú, Volver del navegador, links) con el modal CONFIRM_SALIR, y
 * cierre/recarga de pestaña con el aviso nativo del navegador.
 *
 * Al confirmar el modal el bloqueo se suelta —history exige cero bloqueos para
 * dejar pasar la transición— y se vuelve a registrar cuando la transición
 * aterriza: la vista puede sobrevivir a esa navegación con los cambios intactos,
 * porque las dos rutas que la declaran comparten instancia de componente.
 *
 * Mientras el modal está en pantalla los intentos de salida repetidos (p. ej.
 * varios Atrás seguidos) se descartan, para no encolar modales duplicados en
 * MessageModalContext; y si la vista ya se desmontó, confirmar sólo cierra el
 * modal, sin arrastrar una navegación vieja.
 *
 * @param {boolean} when - true mientras la vista tenga cambios sin guardar
 * @returns {{allowNavigation: Function}} allowNavigation habilita UNA salida
 * programática legítima (post-guardado, Salir): suelta el bloqueo, y la primera
 * transición que aterrice consume ese permiso sin re-armar, para no re-registrar
 * el aviso nativo antes del navigate(0) de los flujos con refresh. Si esa salida
 * no ocurre —window.close() que el navegador rechaza, y el documento sigue
 * vivo— el bloqueo se re-arma solo, así la vista nunca queda sin protección.
 */
export const useUnsavedChangesGuard = (when) => {
    const { showMessageModal } = useManagedContext()

    const mountedRef = useRef(true)
    const unblockRef = useRef(null)
    const modalPendingRef = useRef(false)
    const allowOnceRef = useRef(false)
    const scheduleRearmRef = useRef(null)
    const showMessageModalRef = useRef(showMessageModal)
    showMessageModalRef.current = showMessageModal

    useEffect(() => () => {
        mountedRef.current = false
    }, [])

    useEffect(() => {
        if (!when) return undefined

        allowOnceRef.current = false
        modalPendingRef.current = false

        let rearmTimer = null
        let unloading = false

        const block = () => {
            if (unblockRef.current) return

            unblockRef.current = history.block((tx) => {
                if (modalPendingRef.current) return
                modalPendingRef.current = true

                showMessageModalRef.current({
                    ...MESSAGE_MODALS.CONFIRM_SALIR,
                    onConfirm: () => {
                        modalPendingRef.current = false
                        if (!mountedRef.current) return

                        unblockRef.current?.()
                        unblockRef.current = null
                        tx.retry()
                    },
                    onDismiss: () => {
                        modalPendingRef.current = false
                    },
                })
            })
        }

        const scheduleRearm = () => {
            clearTimeout(rearmTimer)
            rearmTimer = setTimeout(() => {
                if (unloading || !mountedRef.current) return
                if (!allowOnceRef.current) return

                allowOnceRef.current = false
                block()
            }, CLOSE_REARM_DELAY)
        }
        scheduleRearmRef.current = scheduleRearm

        const onPageHide = () => { unloading = true }
        const onPageShow = () => { unloading = false }
        window.addEventListener("pagehide", onPageHide)
        window.addEventListener("pageshow", onPageShow)

        block()

        const unlisten = history.listen(() => {
            if (allowOnceRef.current) {
                allowOnceRef.current = false
                return
            }

            block()
        })

        return () => {
            unlisten()
            window.removeEventListener("pagehide", onPageHide)
            window.removeEventListener("pageshow", onPageShow)
            clearTimeout(rearmTimer)
            scheduleRearmRef.current = null
            unblockRef.current?.()
            unblockRef.current = null
            modalPendingRef.current = false
            allowOnceRef.current = false
        }
    }, [when])

    const allowNavigation = () => {
        allowOnceRef.current = true
        unblockRef.current?.()
        unblockRef.current = null
        scheduleRearmRef.current?.()
    }

    return { allowNavigation }
}
